import { IBulkImportSummary } from "../../../../@types/admin/AdminHospitalDetails";
import { useBulkImportStatus } from "../../../../hooks/queries/admin/hospitalDetails";
import { getLongFormattedDate } from "../../../../utils/dateFormatter";
import {
  ErrorMessage,
  FieldHint,
  LoadingOverlay,
  ModalContainer,
  ModalHeader,
  ModalSubtitle,
  ModalTitle,
  StatusBadge,
} from "../../shared";
import {
  CountsRow,
  RowNumber,
  RowResultItem,
  RowResultList,
  SummaryItem,
  SummaryLabel,
  SummaryValue,
} from "./HospitalDetails.styles";

/** Badge tone for a job's status: running is neither success nor failure. */
function statusTone(status: string): "active" | "warning" | "error" {
  if (status === "failed") return "error";
  if (status === "completed") return "active";
  return "warning";
}

interface IBulkImportJobModalProps {
  jobId: string;
  /** The list row, when the dialog was opened from it — names the target ward. */
  summary?: IBulkImportSummary;
  wardName?: string;
}

/**
 * One bulk import job and what happened to each row (EN-5175).
 *
 * ⚠️ The acceptance criterion this exists for: **import errors must be
 * readable.** The API returns a per-row result with `rowNumber`, the email it
 * tried, and an `errorMessage` for each failure; Retool showed the job's counts
 * and left the operator to guess which rows failed and why. Every failed row is
 * listed here with its number and reason, failures first.
 *
 * Polls while the job is unfinished — the import runs in the background, so a
 * job opened straight after upload has no results yet. Retool made the operator
 * press Refresh.
 */
export function BulkImportJobModal({
  jobId,
  summary,
  wardName,
}: IBulkImportJobModalProps) {
  const { job, isLoading, isError } = useBulkImportStatus(jobId);

  const status = (job?.status ?? summary?.status ?? "").toLowerCase();
  const isRunning = status !== "completed" && status !== "failed";

  const results = job?.results ?? [];
  // Failures first: they are the only rows anyone needs to act on.
  const ordered = [...results].sort((a, b) => {
    if (a.success !== b.success) return a.success ? 1 : -1;
    return a.rowNumber - b.rowNumber;
  });

  return (
    <ModalContainer>
      <ModalHeader>
        <ModalTitle>Import result</ModalTitle>
        <ModalSubtitle>
          Job {jobId}
          {wardName ? ` · ${wardName}` : ""}
          {summary?.created
            ? ` · queued ${getLongFormattedDate(summary.created, "-")}`
            : ""}
        </ModalSubtitle>
      </ModalHeader>

      {isError && (
        <ErrorMessage>
          Failed to load this import. Close the dialog and try again.
        </ErrorMessage>
      )}

      {isLoading && !job && <LoadingOverlay>Loading import...</LoadingOverlay>}

      {job && (
        <>
          <CountsRow>
            <SummaryItem>
              <SummaryLabel>Status</SummaryLabel>
              <SummaryValue>
                <StatusBadge status={statusTone(status)}>
                  {job.status}
                </StatusBadge>
              </SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel>Rows</SummaryLabel>
              <SummaryValue>{job.totalRows}</SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel>Processed</SummaryLabel>
              <SummaryValue>{job.processedRows}</SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel>Created</SummaryLabel>
              <SummaryValue>{job.succeededRows}</SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel>Failed</SummaryLabel>
              <SummaryValue>{job.failedRows}</SummaryValue>
            </SummaryItem>
          </CountsRow>

          {isRunning && (
            <FieldHint>
              Still running — this updates itself, there is nothing to press.
            </FieldHint>
          )}

          {/* A job-level failure (an unreadable file, say) has no row results
              at all, so its message is the only thing there is to show. */}
          {job.errorMessage && <ErrorMessage>{job.errorMessage}</ErrorMessage>}

          {ordered.length === 0 && !isRunning && !job.errorMessage && (
            <FieldHint>This import produced no row results.</FieldHint>
          )}

          {ordered.length > 0 && (
            <RowResultList>
              {ordered.map((row) => (
                <RowResultItem
                  key={`${row.rowNumber}-${row.email}`}
                  failed={!row.success}
                >
                  <RowNumber>Row {row.rowNumber}</RowNumber>
                  <span>
                    {[row.firstName, row.lastName].filter(Boolean).join(" ") ||
                      row.email}
                    {row.email ? ` · ${row.email}` : ""}
                    {row.success
                      ? ` · created as patient ${row.patientId}`
                      : ` · ${row.errorMessage || "failed"}`}
                  </span>
                </RowResultItem>
              ))}
            </RowResultList>
          )}
        </>
      )}
    </ModalContainer>
  );
}
